import { Fragment, useContext, useMemo, useState } from 'react';

import { PhoebeChatOrganizationScopeContext } from '@apps/web/components/phoebe_chat/chat_organization_scope';
import {
    type DynamicWidgetActionRequest,
    type DynamicWidgetActionStateResolver,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_actions';
import { dynamicWidgetBindingProps } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_binding_dom';
import { DynamicWidgetIssueNotice } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_issue_notice';
import { type ShiftListWidgetModel } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_schema';
import {
    ShiftIssueSection,
    ShiftListFilters,
    ShiftListFooter,
    ShiftListHeader,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/shift_list_controls';
import {
    buildShiftListViewModel,
    formatShiftDayHeading,
    getActionIssue,
    organizationLocalToday,
    type ShiftListDateFilter,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/shift_list_view_model';
import { ShiftRow } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/shift_row';
import {
    TEMPLATE_CARD_CLASS,
    TEMPLATE_DAY_HEADER_CLASS,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/template_theme';
import { useShiftWidgetActions } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/use_shift_widget_actions';

export function ShiftListWidget({
    model,
    onCaregiverClick,
    onWidgetAction,
    onViewOutreach,
    getActionState,
    totalRowCount = model.rowCount,
    hasMoreRows = false,
    isLoadingMoreRows = false,
    onShowAllRows,
    allowBulkAction = true,
}: {
    readonly model: ShiftListWidgetModel;
    readonly onCaregiverClick?: (caregiverId: string) => void;
    readonly onWidgetAction?: (
        action: DynamicWidgetActionRequest,
    ) => Promise<boolean | void> | boolean | void;
    readonly onViewOutreach?: (
        shiftId: string,
    ) => Promise<boolean | void> | boolean | void;
    readonly getActionState?: DynamicWidgetActionStateResolver;
    readonly totalRowCount?: number;
    readonly hasMoreRows?: boolean;
    readonly isLoadingMoreRows?: boolean;
    readonly onShowAllRows?: () => void;
    readonly allowBulkAction?: boolean;
}) {
    const organizationScope = useContext(PhoebeChatOrganizationScopeContext);
    const [dateFilter, setDateFilter] = useState<ShiftListDateFilter>('all');
    const [clientFilter, setClientFilter] = useState<string | null>(null);
    const [locationFilter, setLocationFilter] = useState<string | null>(null);
    const [showAllShifts, setShowAllShifts] = useState(false);
    const timezone =
        organizationScope?.timezone ??
        Intl.DateTimeFormat().resolvedOptions().timeZone;
    const today = useMemo(() => organizationLocalToday(timezone), [timezone]);
    const view = useMemo(
        () =>
            buildShiftListViewModel({
                model,
                filters: {
                    date: dateFilter,
                    client: clientFilter,
                    location: locationFilter,
                },
                today,
                showAllShifts,
            }),
        [clientFilter, dateFilter, locationFilter, model, showAllShifts, today],
    );
    const actions = useShiftWidgetActions({
        model,
        onWidgetAction,
        getActionState,
    });
    const bulkActionState = actions.getRenderedActionState(view.bulkRows);
    const bulkActionIssue = getActionIssue(model, bulkActionState.state, [
        'bulkShiftIds',
    ]);

    if (model.rows.length === 0) {
        const sectionIssues = model.issues.filter(
            (issue) => issue.scope === 'section',
        );
        return (
            <Fragment>
                <ShiftIssueSection
                    issues={view.widgetIssues}
                    metricTags={actions.metricTags}
                    placement="widget"
                />
                <div className={TEMPLATE_CARD_CLASS}>
                    {view.showHeaderCount ? (
                        <ShiftListHeader model={model} />
                    ) : null}
                    <div className="space-y-2 p-3.5">
                        {sectionIssues.map((issue) => (
                            <DynamicWidgetIssueNotice
                                key={issue.id}
                                issue={issue}
                                metricTags={actions.metricTags}
                            />
                        ))}
                        <p className="text-foreground text-sm font-medium">
                            No shifts found
                        </p>
                        {model.note ? (
                            <p className="text-muted-foreground mt-0.5 text-xs">
                                {model.note}
                            </p>
                        ) : null}
                    </div>
                </div>
            </Fragment>
        );
    }

    const showFooter =
        view.hiddenCount > 0 ||
        hasMoreRows ||
        (allowBulkAction && model.bulkOpenShiftCount >= 2) ||
        model.note !== null;

    return (
        <Fragment>
            <ShiftIssueSection
                issues={view.widgetIssues}
                metricTags={actions.metricTags}
                placement="widget"
            />
            <div className={TEMPLATE_CARD_CLASS}>
                {view.showHeaderCount ? (
                    <ShiftListHeader model={model} />
                ) : null}
                <ShiftIssueSection
                    issues={view.contentIssues}
                    metricTags={actions.metricTags}
                    placement="content"
                />
                <ShiftListFilters
                    dateFilter={dateFilter}
                    clientFilter={clientFilter}
                    locationFilter={locationFilter}
                    clientOptions={view.clientOptions}
                    locationOptions={view.locationOptions}
                    onDateFilterChange={setDateFilter}
                    onClientFilterChange={setClientFilter}
                    onLocationFilterChange={setLocationFilter}
                />
                {view.dayGroups.length === 0 ? (
                    <p className="text-muted-foreground px-3.5 py-3 text-xs">
                        No shifts match these filters
                    </p>
                ) : null}
                {view.dayGroups.map((group) => (
                    <Fragment
                        key={`${group.day ?? 'unknown'}-${group.groupIndex}`}
                    >
                        <p
                            className={TEMPLATE_DAY_HEADER_CLASS}
                            {...dynamicWidgetBindingProps(
                                model.widgetId,
                                `dayGroups.${group.groupIndex}.dayLabel`,
                            )}
                        >
                            {formatShiftDayHeading(group.day, group.dayLabel)}
                        </p>
                        {group.rows.map((rowView) => (
                            <ShiftRow
                                key={rowView.row.shiftId}
                                model={model}
                                view={rowView}
                                actionState={actions.getRenderedActionState([
                                    rowView.row,
                                ])}
                                metricTags={actions.metricTags}
                                onCaregiverClick={onCaregiverClick}
                                actionsEnabled={onWidgetAction !== undefined}
                                onViewOutreach={onViewOutreach}
                                onRequestOutreach={actions.requestShiftOutreach}
                            />
                        ))}
                    </Fragment>
                ))}
                {showFooter ? (
                    <ShiftListFooter
                        model={model}
                        hiddenCount={view.hiddenCount}
                        totalRowCount={totalRowCount}
                        hasMoreRows={hasMoreRows}
                        isLoadingMoreRows={isLoadingMoreRows}
                        allowBulkAction={allowBulkAction}
                        bulkRows={view.bulkRows}
                        bulkActionState={bulkActionState}
                        bulkActionIssue={bulkActionIssue}
                        bulkModelActionIssues={view.bulkActionIssues}
                        metricTags={actions.metricTags}
                        actionsEnabled={onWidgetAction !== undefined}
                        onShowAll={() => {
                            setShowAllShifts(true);
                            onShowAllRows?.();
                        }}
                        onRequestOutreach={actions.requestShiftOutreach}
                    />
                ) : null}
            </div>
        </Fragment>
    );
}
